/**
 * Modal for generating and previewing email invite with GIF teaser.
 */

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import DOMPurify from "dompurify";

interface EmailExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  narrativeUrl: string;
  totalGroups: number;
  shareToken?: string;
}

type GenerationStatus =
  | "idle"
  | "checking"
  | "generating"
  | "success"
  | "error";

export function EmailExportModal({
  isOpen,
  onClose,
  reportId,
  narrativeUrl,
  totalGroups,
  shareToken,
}: EmailExportModalProps) {
  const [status, setStatus] = useState<GenerationStatus>("checking");
  const [gifUrl, setGifUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedPlain, setCopiedPlain] = useState(false);

  // PDF generation state
  const [pdfStatus, setPdfStatus] = useState<GenerationStatus>("idle");
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfProgress, setPdfProgress] = useState<string>("");

  const checkExistingGif = useCallback(async () => {
    setStatus("checking");
    // TODO: Consider project-specific export folders (e.g., /src/narratives/[project]/exports/)
    const existingUrl = `/src/narratives/exports/${reportId}-preview.gif`;

    try {
      const response = await fetch(existingUrl, { method: "HEAD" });
      // Check both that request succeeded AND that it's actually a GIF (not an HTML fallback)
      const contentType = response.headers.get("content-type") || "";
      if (response.ok && contentType.includes("image/gif")) {
        // GIF exists, use it
        setGifUrl(`${existingUrl}?t=${Date.now()}`);
        setStatus("success");
      } else {
        // GIF doesn't exist, show idle state
        setStatus("idle");
      }
    } catch {
      // Error checking, show idle state
      setStatus("idle");
    }
  }, [reportId]);

  // Check if GIF already exists when modal opens
  useEffect(() => {
    if (isOpen) {
      checkExistingGif();
    }
  }, [isOpen, checkExistingGif]);

  const generateGif = async () => {
    setStatus("generating");
    setError(null);

    try {
      const response = await fetch("/api/generate-gif", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reportId,
          numPages: Math.min(totalGroups, 3),
          shareToken,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setGifUrl(`${result.gifUrl}?t=${Date.now()}`);
        setStatus("success");
      } else {
        setError(result.error || "Failed to generate GIF");
        setStatus("error");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error");
      setStatus("error");
    }
  };

  const getEmailHtml = () => {
    // Encode URLs to prevent XSS via special characters
    const fullGifUrl = gifUrl
      ? encodeURI(`${window.location.origin}${gifUrl.split("?")[0]}`)
      : "";
    const fullNarrativeUrl = encodeURI(
      `${window.location.origin}${narrativeUrl}`,
    );

    return `<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="400" style="max-width: 400px; border-radius: 16px; overflow: hidden;">
  <tr>
    <td style="background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%); border-radius: 16px; overflow: hidden;">
      <!-- GIF Preview -->
      <a href="${fullNarrativeUrl}" target="_blank" style="display: block; text-decoration: none; line-height: 0;">
        <img src="${fullGifUrl}" alt="Preview your insights" width="400" height="400" style="display: block; width: 400px; max-width: 100%; height: auto; border: 0; outline: none; border-radius: 16px 16px 0 0;">
      </a>
      
      <!-- CTA Section -->
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr>
          <td style="padding: 20px 24px 24px;">
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
              <tr>
                <td align="center">
                  <a href="${fullNarrativeUrl}" target="_blank" style="display: inline-block; background-color: #10B981; color: #ffffff; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 16px; font-weight: 600; text-decoration: none; padding: 14px 32px; border-radius: 50px;">
                    📱 View Briefing
                  </a>
                </td>
              </tr>
            </table>
            <p style="margin: 16px 0 0; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 13px; color: #94a3b8;">
              Best viewed on mobile • Takes ~2 min to explore
            </p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
  };

  const copyHtml = async () => {
    const html = getEmailHtml();

    try {
      // Use clipboard API with text/html MIME type for rich paste in Gmail
      if (navigator.clipboard && navigator.clipboard.write) {
        const blob = new Blob([html], { type: "text/html" });
        const clipboardItem = new ClipboardItem({
          "text/html": blob,
          "text/plain": new Blob([html], { type: "text/plain" }),
        });
        await navigator.clipboard.write([clipboardItem]);
      } else {
        // Fallback: use a contenteditable div to copy as rich HTML
        const div = document.createElement("div");
        div.innerHTML = html;
        div.style.position = "fixed";
        div.style.left = "-9999px";
        div.setAttribute("contenteditable", "true");
        document.body.appendChild(div);

        const range = document.createRange();
        range.selectNodeContents(div);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);

        document.execCommand("copy");
        document.body.removeChild(div);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const getSimpleHtmlEmail = () => {
    // Encode URLs to prevent XSS via special characters
    const fullGifUrl = gifUrl
      ? encodeURI(`${window.location.origin}${gifUrl.split("?")[0]}`)
      : "";
    const fullNarrativeUrl = encodeURI(
      `${window.location.origin}${narrativeUrl}`,
    );

    return `<div style="background-color: #ffffff; background: #ffffff;">
<p style="margin: 0 0 12px 0;"><a href="${fullNarrativeUrl}"><img src="${fullGifUrl}" alt="Preview" width="400" style="max-width: 100%; border-radius: 12px; display: block;"></a></p>
<p style="margin: 0 0 8px 0; color: #000000;">📱 <a href="${fullNarrativeUrl}" style="color: #0066cc;">View Briefing</a></p>
<p style="margin: 0; color: #666666; font-size: 13px;">Best viewed on mobile • Takes ~2 min to explore</p>
</div>`;
  };

  const copyPlainText = async () => {
    const html = getSimpleHtmlEmail();

    try {
      // Copy as HTML so the image and links render properly
      if (navigator.clipboard && navigator.clipboard.write) {
        const blob = new Blob([html], { type: "text/html" });
        const clipboardItem = new ClipboardItem({
          "text/html": blob,
          "text/plain": new Blob([html], { type: "text/plain" }),
        });
        await navigator.clipboard.write([clipboardItem]);
      } else {
        // Fallback: use a contenteditable div to copy as rich HTML
        const div = document.createElement("div");
        div.innerHTML = html;
        div.style.position = "fixed";
        div.style.left = "-9999px";
        div.setAttribute("contenteditable", "true");
        document.body.appendChild(div);

        const range = document.createRange();
        range.selectNodeContents(div);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);

        document.execCommand("copy");
        document.body.removeChild(div);
      }
      setCopiedPlain(true);
      setTimeout(() => setCopiedPlain(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const downloadGif = () => {
    if (gifUrl) {
      const a = document.createElement("a");
      a.href = gifUrl;
      a.download = `${reportId}-preview.gif`;
      a.click();
    }
  };

  const checkExistingPdf = useCallback(async () => {
    // TODO: Consider project-specific export folders (e.g., /src/narratives/[project]/exports/)
    const existingUrl = `/src/narratives/exports/${reportId}.pdf`;

    try {
      const response = await fetch(existingUrl, { method: "HEAD" });
      const contentType = response.headers.get("content-type") || "";
      if (response.ok && contentType.includes("application/pdf")) {
        // PDF exists, use it
        setPdfUrl(`${existingUrl}?t=${Date.now()}`);
        setPdfStatus("success");
      } else {
        // PDF doesn't exist, show idle state
        setPdfStatus("idle");
      }
    } catch {
      // Error checking, show idle state
      setPdfStatus("idle");
    }
  }, [reportId]);

  // Check if PDF already exists when modal opens
  useEffect(() => {
    if (isOpen) {
      checkExistingPdf();
    }
  }, [isOpen, checkExistingPdf]);

  const generatePdf = async () => {
    setPdfStatus("generating");
    setPdfProgress("Preparing...");

    try {
      const response = await fetch("/api/generate-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reportId, numPages: totalGroups, shareToken }),
      });

      const result = await response.json();

      if (result.success) {
        setPdfUrl(`${result.pdfUrl}?t=${Date.now()}`);
        setPdfStatus("success");
        setPdfProgress("");
      } else {
        setPdfStatus("error");
        setPdfProgress("");
      }
    } catch (err) {
      console.error("PDF generation failed:", err);
      setPdfStatus("error");
      setPdfProgress("");
    }
  };

  const downloadPdf = () => {
    if (pdfUrl) {
      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = `${reportId}.pdf`;
      a.click();
    }
  };

  const handleClose = () => {
    setStatus("checking");
    setGifUrl(null);
    setError(null);
    setCopied(false);
    setCopiedPlain(false);
    setPdfStatus("idle");
    setPdfUrl(null);
    setPdfProgress("");
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-slate-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
              <h2 className="text-xl font-semibold text-white">
                Generate Email Invite
              </h2>
              <button
                onClick={handleClose}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {status === "checking" && (
                <div className="text-center py-12">
                  <div className="w-12 h-12 mx-auto mb-4 border-4 border-slate-600 border-t-emerald-400 rounded-full animate-spin" />
                  <p className="text-slate-400">
                    Checking for existing preview...
                  </p>
                </div>
              )}

              {status === "idle" && (
                <div className="text-center py-8">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-800 flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-emerald-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">
                    Create Email Teaser
                  </h3>
                  <p className="text-slate-400 mb-6 max-w-md mx-auto">
                    {import.meta.env.DEV
                      ? "Generate an animated GIF preview of the first 3 pages and get ready-to-paste HTML for your email."
                      : "No preview GIF found. GIF generation is only available in local development mode."}
                  </p>
                  {import.meta.env.DEV && (
                    <button
                      onClick={generateGif}
                      className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full transition-colors"
                    >
                      Generate Preview
                    </button>
                  )}
                </div>
              )}

              {status === "generating" && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                  <h3 className="text-lg font-medium text-white mb-2">
                    Generating GIF...
                  </h3>
                  <p className="text-slate-400">
                    Capturing pages and encoding animation
                  </p>
                  <p className="text-slate-500 text-sm mt-2">
                    This may take 15-30 seconds
                  </p>
                </div>
              )}

              {status === "error" && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-red-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">
                    Generation Failed
                  </h3>
                  <p className="text-slate-400 mb-4">{error}</p>
                  <button
                    onClick={generateGif}
                    className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-full transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {status === "success" && gifUrl && (
                <div className="space-y-6">
                  {/* Email Preview - Full rendered email */}
                  <div>
                    <h3 className="text-sm font-medium text-slate-400 mb-3 text-center">
                      Email Preview
                    </h3>
                    <div className="rounded-xl overflow-hidden bg-[#f5f5f5] p-4 flex justify-center">
                      {/* Render the actual email HTML */}
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(getEmailHtml()),
                        }}
                        style={{
                          fontFamily:
                            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        }}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={copyHtml}
                      className="flex-1 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                    >
                      {copied ? (
                        <>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          Copied!
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                            />
                          </svg>
                          Copy Rich
                        </>
                      )}
                    </button>
                    <button
                      onClick={copyPlainText}
                      className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                    >
                      {copiedPlain ? (
                        <>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          Copied!
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          Copy Simple
                        </>
                      )}
                    </button>
                  </div>

                  {/* Download button */}
                  <button
                    onClick={downloadGif}
                    className="w-full px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    Download GIF
                  </button>

                  {/* PDF Download button - dev only */}
                  {import.meta.env.DEV && (
                    <button
                      onClick={
                        pdfStatus === "success" ? downloadPdf : generatePdf
                      }
                      disabled={pdfStatus === "generating"}
                      className="w-full px-4 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-xl text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={
                            pdfStatus === "success"
                              ? "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                              : "M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          }
                        />
                      </svg>
                      {pdfStatus === "generating"
                        ? `Generating PDF... ${pdfProgress}`
                        : pdfStatus === "success"
                          ? "Download PDF"
                          : "Generate PDF"}
                    </button>
                  )}

                  {/* Instructions */}
                  <div className="bg-slate-800 rounded-xl p-4">
                    <h4 className="text-sm font-medium text-white mb-2">
                      How to use
                    </h4>
                    <ol className="text-sm text-slate-400 space-y-1 list-decimal list-inside">
                      <li>Write your personal message in Gmail/Outlook</li>
                      <li>Click "Copy Rich" or "Copy Simple"</li>
                      <li>Paste below your message</li>
                    </ol>
                  </div>

                  {/* Regenerate - development only */}
                  {import.meta.env.DEV && (
                    <button
                      onClick={generateGif}
                      className="w-full px-4 py-2 text-slate-400 hover:text-white text-sm transition-colors"
                    >
                      Regenerate GIF
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
